import type { NavItem } from '@/interfaces/components/navMain.interface';
import type { PermissionRequirement } from '@/interfaces/permission.interface';
import { dataSidebar } from '@/constants';
import { PATHS } from '@/routes';

export type PermissionChecker = (requirement?: PermissionRequirement) => boolean;

const canAccess = (checker: PermissionChecker, requirement?: PermissionRequirement) => {
	if (!requirement) {
		return true;
	}

	return checker(requirement);
};

export const filterNavItemsByPermission = (
	items: NavItem[],
	hasPermission: PermissionChecker,
): NavItem[] => {
	return items
		.map(item => {
			const filteredSubItems = item.items
				?.filter(subItem => canAccess(hasPermission, subItem.requiredPermissions))
				?.map(subItem => ({ ...subItem }));

			const canShowSectionWithoutSubItems =
				(!item.items || item.items.length === 0) &&
				canAccess(hasPermission, item.requiredPermissions);

			if (item.items && item.items.length > 0) {
				if (filteredSubItems && filteredSubItems.length > 0) {
					return { ...item, items: filteredSubItems };
				}
				return null;
			}

			return canShowSectionWithoutSubItems ? { ...item } : null;
		})
		.filter((item): item is NavItem => Boolean(item));
};

export const findFirstAccessiblePath = (
	sections: NavItem[][],
	hasPermission: PermissionChecker,
): string | null => {
	for (const section of sections) {
		const filteredItems = filterNavItemsByPermission(section, hasPermission);

		for (const item of filteredItems) {
			if (item.items && item.items.length > 0) {
				return item.items[0].url;
			}

			if (item.url) {
				return item.url;
			}
		}
	}

	return null;
};

const DEFAULT_SECTIONS: NavItem[][] = [
	dataSidebar.dashboard,
	dataSidebar.searches,
	dataSidebar.navClient,
	dataSidebar.navOrganization,
	dataSidebar.configurations,
];

export const getDefaultHomePath = (
	hasPermission: PermissionChecker,
	fallback: string = PATHS.DASHBOARD_PRODUCT,
): string => {
	// El panel interno tiene prioridad: un usuario con acceso a cualquier sección
	// (admin, gestor, etc.) nunca debe acabar en un portal externo aunque, por su
	// rol, tenga también los permisos de portal.
	const internalPath = findFirstAccessiblePath(DEFAULT_SECTIONS, hasPermission);
	if (internalPath) {
		return internalPath;
	}

	// Cuentas dedicadas de portal: solo tienen permisos de portal (+ auth), por lo
	// que no acceden a ninguna sección interna y se redirigen a su portal.
	if (hasPermission('portal-subcontract.read')) {
		return PATHS.PORTAL_SUBCONTRACT_RESUME;
	}
	if (hasPermission('portal-client.read')) {
		return PATHS.PORTAL_CLIENT_RESUME;
	}
	if (hasPermission('portal-trainer.read')) {
		return PATHS.PORTAL_TRAINER_CALENDAR;
	}

	return fallback;
};
